import React, { useState, useEffect } from 'react';
import { ArrowLeft, Music, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { musicService } from '../services/musicService';
import { authService } from '../services/authService';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

const CreatePlaylist = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cover: 'https://images.unsplash.com/photo-1518818419601-72c8673f5852?w=400'
  });

  // Check auth on mount
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      toast.info('Inicia sesión para crear playlists');
      navigate('/auth');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!authService.isAuthenticated()) {
      toast.error('Debes iniciar sesión para crear playlists');
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      await musicService.createPlaylist(formData.name, formData.description, formData.cover);
      toast.success('¡Playlist creada exitosamente!');
      navigate('/library');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear playlist');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto pb-32 md:pb-24 bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <button
            onClick={() => navigate('/library')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#00CCFF] mb-6 transition-colors duration-200 touch-manipulation"
          >
            <ArrowLeft size={20} />
            <span className="font-semibold text-sm md:text-base">Volver</span>
          </button>

          <h1 className="text-2xl md:text-5xl font-black text-gray-900 mb-6 md:mb-8">Crear Playlist</h1>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-xl p-5 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Nombre de la Playlist
                </label>
                <Input
                  type="text"
                  placeholder="Ej: Mi Adoración Favorita"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-xl text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Descripción
                </label>
                <Textarea
                  placeholder="Describe tu playlist..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="rounded-xl text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  URL de Portada (Opcional)
                </label>
                <div className="relative">
                  <ImageIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <Input
                    type="url"
                    placeholder="https://ejemplo.com/imagen.jpg"
                    value={formData.cover}
                    onChange={(e) => setFormData({ ...formData, cover: e.target.value })}
                    className="pl-12 rounded-xl text-base"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Si no agregas una portada, se usará una imagen predeterminada
                </p>
              </div>

              <div className="flex flex-col md:flex-row gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full md:flex-1 bg-gradient-to-r from-[#00CCFF] to-[#056A84] hover:scale-105 rounded-xl font-bold text-base py-6 md:py-6 touch-manipulation"
                >
                  {loading ? 'Creando...' : 'Crear Playlist'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/library')}
                  className="w-full md:w-auto rounded-xl font-bold px-8 py-6 md:py-6 touch-manipulation"
                >
                  Cancelar
                </Button>
              </div>
            </form>

            {/* Tips */}
            <div className="mt-6 md:mt-8 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
              <p className="text-sm text-gray-700">
                <span className="font-bold">💡 Tip:</span> Después de crear tu playlist, podrás agregar canciones desde cualquier álbum o canción.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePlaylist;
